/**
 * shadcn/ui Tooltip — vendored + tokenized for CertAIn v2 (Phase 1 fan-out foundation).
 *
 * Radix Tooltip (Provider / Root / Trigger / Content). Content uses the elevated
 * surface + pop shadow with primary ink. No raw color — resolves inside
 * `[data-theme="v2"]`. Guard-clean.
 *
 * Used by fit-meter hovers, topbar icon buttons, truncated cells, and any
 * affordance needing a hint across the fan-out screens.
 */
import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "@/lib/utils";
import { usePortalHost } from "@/components/v2/layout/PortalHostContext";

const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => {
  // Mount inside the zoomed shell host so the tooltip matches app scale under
  // the app-scale dial; `?? undefined` falls back to document.body off the shell.
  const container = usePortalHost() ?? undefined;
  return (
  <TooltipPrimitive.Portal container={container}>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        // [zoom:var(--app-scale)] scales the tooltip to match the app-scale dial;
        // the host stays at body-zoom-1 so positioning is correct. See
        // PortalHostContext.
        "[zoom:var(--app-scale)]",
        "z-50 overflow-hidden rounded-[calc(var(--radius,0.5rem)-2px)] border border-border-default bg-surface-elevated px-2.5 py-1.5 text-[12px] text-text-primary shadow-[var(--shadow-pop)]",
        "data-[state=delayed-open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=delayed-open]:fade-in-0",
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
  );
});
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
